import { Fragment, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import "./css/Chatbot.css";

const GREETING = {
  role: "assistant",
  content:
    "Hi! 👋 I'm Daniel's assistant. Ask me about his IT systems engineering, software projects, or how to get in touch.",
};

const SUGGESTIONS = [
  "What does Daniel do?",
  "What are his certifications?",
  "Tell me about his projects",
  "How can I contact him?",
];

const ttsSupported =
  typeof window !== "undefined" && "speechSynthesis" in window;

/* ---------- tiny markdown renderer (bold, bullet & numbered lists) ---------- */
function renderInline(text, keyBase) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    const em = part.match(/^\*([^*]+)\*$/);
    if (bold) return <strong key={`${keyBase}-${i}`}>{bold[1]}</strong>;
    if (em) return <em key={`${keyBase}-${i}`}>{em[1]}</em>;
    return <Fragment key={`${keyBase}-${i}`}>{part}</Fragment>;
  });
}

function FormattedMessage({ text }) {
  const blocks = [];
  let list = null;
  const flush = () => {
    if (list) {
      blocks.push(list);
      list = null;
    }
  };

  text.split(/\n/).forEach((raw) => {
    const line = raw.trim();
    if (!line) return flush();
    const ul = line.match(/^[-*]\s+(.*)/);
    const ol = line.match(/^\d+\.\s+(.*)/);
    if (ul) {
      if (!list || list.type !== "ul") {
        flush();
        list = { type: "ul", items: [] };
      }
      list.items.push(ul[1]);
    } else if (ol) {
      if (!list || list.type !== "ol") {
        flush();
        list = { type: "ol", items: [] };
      }
      list.items.push(ol[1]);
    } else {
      flush();
      blocks.push({ type: "p", text: line });
    }
  });
  flush();

  return blocks.map((b, i) => {
    if (b.type === "p")
      return <p key={i}>{renderInline(b.text, `p${i}`)}</p>;
    const Tag = b.type === "ol" ? "ol" : "ul";
    return (
      <Tag key={i}>
        {b.items.map((it, j) => (
          <li key={j}>{renderInline(it, `l${i}-${j}`)}</li>
        ))}
      </Tag>
    );
  });
}

const stripMarkdown = (t) =>
  t
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/^[\s]*[-*]\s+/gm, "")
    .replace(/^[\s]*\d+\.\s+/gm, "")
    .replace(/[#`>]/g, "");

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [speakingIndex, setSpeakingIndex] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, loading, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Stop any speech when the widget closes or unmounts.
  useEffect(() => {
    if (!open && ttsSupported) {
      window.speechSynthesis.cancel();
      setSpeakingIndex(null);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (ttsSupported) window.speechSynthesis.cancel();
    };
  }, []);

  const speak = (text, index) => {
    if (!ttsSupported) return;
    window.speechSynthesis.cancel();
    if (speakingIndex === index) {
      setSpeakingIndex(null);
      return;
    }
    const utter = new SpeechSynthesisUtterance(stripMarkdown(text));
    utter.rate = 1;
    utter.pitch = 1;
    utter.onend = () => setSpeakingIndex(null);
    utter.onerror = () => setSpeakingIndex(null);
    setSpeakingIndex(index);
    window.speechSynthesis.speak(utter);
  };

  const send = async (text) => {
    const content = text.trim();
    if (!content || loading) return;

    const next = [...messages, { role: "user", content }];
    setMessages(next);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: next.filter((m) => m !== GREETING),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Request failed");
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Sorry, I couldn't reach the assistant right now. You can email Daniel at danieloshinubi@gmail.com.",
          error: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    send(input);
  };

  return (
    <>
      <button
        className={`chat-fab ${open ? "is-open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close chat" : "Open chat"}
      >
        {open ? "✕" : "💬"}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            role="dialog"
            aria-label="Chat with Daniel's assistant"
          >
            <header className="chat-header">
              <span className="chat-header__avatar">AO</span>
              <span className="chat-header__meta">
                <strong>Daniel&apos;s Assistant</strong>
                <span className="chat-header__status">
                  <span className="chat-header__dot" /> Online
                </span>
              </span>
              <button
                className="chat-header__close"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
              >
                ✕
              </button>
            </header>

            <div className="chat-messages" ref={scrollRef}>
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`chat-row chat-row--${m.role}`}
                >
                  <div
                    className={`chat-bubble chat-bubble--${m.role} ${
                      m.error ? "is-error" : ""
                    }`}
                  >
                    {m.role === "assistant" ? (
                      <div className="chat-md">
                        <FormattedMessage text={m.content} />
                      </div>
                    ) : (
                      m.content
                    )}
                  </div>

                  {m.role === "assistant" && !m.error && ttsSupported && (
                    <button
                      className={`chat-speak ${
                        speakingIndex === i ? "is-speaking" : ""
                      }`}
                      onClick={() => speak(m.content, i)}
                      aria-label={
                        speakingIndex === i ? "Stop reading" : "Read aloud"
                      }
                      title={speakingIndex === i ? "Stop" : "Read aloud"}
                    >
                      {speakingIndex === i ? "⏹" : "🔊"}
                    </button>
                  )}
                </div>
              ))}

              {loading && (
                <div className="chat-row chat-row--assistant">
                  <div className="chat-bubble chat-bubble--assistant chat-typing">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              )}

              {messages.length === 1 && !loading && (
                <div className="chat-suggestions">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} type="button" onClick={() => send(s)}>
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form className="chat-input" onSubmit={onSubmit}>
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about Daniel…"
                aria-label="Message"
              />
              <button type="submit" disabled={!input.trim() || loading} aria-label="Send">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 11.5 21 3l-8.5 18-2.5-7.5L3 11.5Z" />
                </svg>
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
